import type {
  AssignableLease,
  AuditPage,
  Landlord,
  LeaseDocument,
  LeaseInput,
  LeaseListRow,
  LeasePremiseUse,
  MarketingIndividual,
  PremiseUseInput,
  Property,
  ApplyCodesResult,
  ReconDetail,
  ReconListRow,
  ReconSummary,
  ReconUploadOutcome,
  ReconUploadResult,
  RentPeriod,
  LeaseRenewal,
  RenewLeaseInput,
  RenewLeaseResult,
  RentRollCheckResponse,
  SessionUser,
  SimilarMatch,
  StaffUser,
  Tenant,
  UpcomingExpiration,
} from './types';

export type SaveResult =
  | { ok: true; row: LeasePremiseUse }
  | { ok: false; similar: SimilarMatch[] };

export type TenantSaveResult =
  | { ok: true; row: Tenant }
  | { ok: false; reason: 'similar'; similar: SimilarMatch[] }
  | { ok: false; reason: 'duplicate_tpin'; tpin: string | null };

export type DeleteResult = { ok: true } | { ok: false; inUse: number };

const json = async (path: string, init?: RequestInit): Promise<Response> => {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  return res;
};

const asJson = async <T>(res: Response): Promise<T> => {
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText} ${text}`);
  }
  return (await res.json()) as T;
};

const tenantConflict = async (res: Response): Promise<TenantSaveResult> => {
  const body = (await res.json()) as {
    error: string;
    similar?: SimilarMatch[];
    tpin?: string | null;
  };
  if (body.error === 'duplicate_tpin') {
    return { ok: false, reason: 'duplicate_tpin', tpin: body.tpin ?? null };
  }
  return { ok: false, reason: 'similar', similar: body.similar ?? [] };
};

export const api = {
  // auth
  me: () => json('/api/auth/me').then(asJson<{ user: SessionUser }>),
  login: (email: string, password: string) =>
    json('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }).then(asJson<{ user: SessionUser }>),
  logout: () => json('/api/auth/logout', { method: 'POST' }).then(asJson<{ ok: true }>),

  // dashboard
  leaseCounts: () =>
    json('/api/dashboard/lease-counts').then(asJson<{ name: string; lease_count: number }[]>),
  currentMonthExpirations: () =>
    json('/api/dashboard/current-month-expirations').then(asJson<Record<string, unknown>[]>),
  upcomingExpirations: (days = 90) =>
    json(`/api/dashboard/upcoming-expirations?days=${days}`).then(
      asJson<UpcomingExpiration[]>,
    ),

  // leases
  leases: (includeDeleted = false) =>
    json(`/api/leases${includeDeleted ? '?includeDeleted=true' : ''}`).then(
      asJson<LeaseListRow[]>,
    ),
  lease: (id: number) => json(`/api/leases/${id}`).then(asJson<LeaseListRow>),
  createLease: (input: LeaseInput) =>
    json('/api/leases', { method: 'POST', body: JSON.stringify(input) }).then(
      asJson<LeaseListRow>,
    ),
  updateLease: (id: number, input: LeaseInput) =>
    json(`/api/leases/${id}`, { method: 'PATCH', body: JSON.stringify(input) }).then(
      asJson<LeaseListRow>,
    ),
  leaseRentSchedule: (id: number) =>
    json(`/api/leases/${id}/rent-schedule`).then(asJson<RentPeriod[]>),
  deleteLease: (id: number) =>
    json(`/api/leases/${id}`, { method: 'DELETE' }).then(asJson<{ ok: true }>),
  restoreLease: (id: number) =>
    json(`/api/leases/${id}/restore`, { method: 'POST' }).then(asJson<{ ok: true }>),
  renewLease: (id: number, input: RenewLeaseInput) =>
    json(`/api/leases/${id}/renew`, { method: 'POST', body: JSON.stringify(input) }).then(
      asJson<RenewLeaseResult>,
    ),
  leaseRenewals: (id: number) =>
    json(`/api/leases/${id}/renewals`).then(asJson<LeaseRenewal[]>),

  // tenants
  tenants: () => json('/api/tenants').then(asJson<Tenant[]>),
  createTenant: async (input: Partial<Tenant>, force = false): Promise<TenantSaveResult> => {
    const res = await json(`/api/tenants${force ? '?force=true' : ''}`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    if (res.status === 409) return tenantConflict(res);
    return { ok: true, row: await asJson<Tenant>(res) };
  },
  updateTenant: async (
    id: number,
    input: Partial<Tenant>,
    force = false,
  ): Promise<TenantSaveResult> => {
    const res = await json(`/api/tenants/${id}${force ? '?force=true' : ''}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    if (res.status === 409) return tenantConflict(res);
    return { ok: true, row: await asJson<Tenant>(res) };
  },
  deleteTenant: (id: number) =>
    json(`/api/tenants/${id}`, { method: 'DELETE' }).then(asJson<{ ok: true }>),

  // properties
  properties: () => json('/api/properties').then(asJson<Property[]>),
  createProperty: (input: Partial<Property>) =>
    json('/api/properties', { method: 'POST', body: JSON.stringify(input) }).then(
      asJson<Property>,
    ),
  updateProperty: (id: number, input: Partial<Property>) =>
    json(`/api/properties/${id}`, { method: 'PATCH', body: JSON.stringify(input) }).then(
      asJson<Property>,
    ),
  deleteProperty: (id: number) =>
    json(`/api/properties/${id}`, { method: 'DELETE' }).then(asJson<{ ok: true }>),

  // lookups
  landlords: () => json('/api/landlords').then(asJson<Landlord[]>),
  marketingIndividuals: () =>
    json('/api/marketing-individuals').then(asJson<MarketingIndividual[]>),
  leasePremiseUses: () => json('/api/lease-premise-uses').then(asJson<LeasePremiseUse[]>),
  createPremiseUse: async (input: PremiseUseInput, force = false): Promise<SaveResult> => {
    const res = await json(`/api/lease-premise-uses${force ? '?force=true' : ''}`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    if (res.status === 409) {
      const body = (await res.json()) as { similar: SimilarMatch[] };
      return { ok: false, similar: body.similar };
    }
    return { ok: true, row: await asJson<LeasePremiseUse>(res) };
  },
  updatePremiseUse: async (
    id: number,
    input: Partial<PremiseUseInput>,
    force = false,
  ): Promise<SaveResult> => {
    const res = await json(`/api/lease-premise-uses/${id}${force ? '?force=true' : ''}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    if (res.status === 409) {
      const body = (await res.json()) as { similar: SimilarMatch[] };
      return { ok: false, similar: body.similar };
    }
    return { ok: true, row: await asJson<LeasePremiseUse>(res) };
  },
  deletePremiseUse: async (id: number): Promise<DeleteResult> => {
    const res = await json(`/api/lease-premise-uses/${id}`, { method: 'DELETE' });
    if (res.status === 409) {
      const body = (await res.json()) as { count: number };
      return { ok: false, inUse: body.count };
    }
    await asJson<{ ok: true }>(res);
    return { ok: true };
  },

  // documents
  documents: (leaseId: number) =>
    json(`/api/documents/by-lease/${leaseId}`).then(asJson<LeaseDocument[]>),
  uploadDocument: async (leaseId: number, file: File, documentType: string) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('document_type', documentType);
    const res = await fetch(`/api/documents/by-lease/${leaseId}`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    return asJson<LeaseDocument>(res);
  },
  deleteDocument: (id: number) =>
    json(`/api/documents/${id}`, { method: 'DELETE' }).then(asJson<{ ok: true }>),

  // audit
  audit: (page = 1, limit = 50, entityType?: string) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (entityType) params.set('entityType', entityType);
    return json(`/api/audit?${params}`).then(asJson<AuditPage>);
  },

  // reconciliations
  reconciliations: () => json('/api/reconciliations').then(asJson<ReconListRow[]>),
  reconciliation: (id: number) =>
    json(`/api/reconciliations/${id}`).then(asJson<ReconDetail>),
  uploadReconciliation: async (
    csv: File,
    propertyId: number,
    force = false,
  ): Promise<ReconUploadOutcome> => {
    const fd = new FormData();
    fd.append('csv', csv);
    fd.append('propertyId', String(propertyId));
    const res = await fetch(`/api/reconciliations${force ? '?force=true' : ''}`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (res.status === 409) {
      const body = (await res.json()) as { error: string; detected: string[]; chosen: string };
      if (body.error === 'property_text_mismatch') {
        return { ok: false, mismatch: { detected: body.detected, chosen: body.chosen } };
      }
    }
    return { ok: true, result: await asJson<ReconUploadResult>(res) };
  },
  deleteReconciliation: (id: number) =>
    json(`/api/reconciliations/${id}`, { method: 'DELETE' }).then(asJson<{ ok: true }>),
  rentRollCheck: async (rentroll: File, propertyId: number): Promise<RentRollCheckResponse> => {
    const fd = new FormData();
    fd.append('rentroll', rentroll);
    fd.append('propertyId', String(propertyId));
    const res = await fetch('/api/reconciliations/rent-roll-check', {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    return asJson<RentRollCheckResponse>(res);
  },
  applyCodes: (mappings: { leaseId: number; code: string }[]) =>
    json('/api/reconciliations/apply-codes', {
      method: 'POST',
      body: JSON.stringify({ mappings }),
    }).then(asJson<ApplyCodesResult>),
  reconAssignableLeases: (id: number) =>
    json(`/api/reconciliations/${id}/assignable-leases`).then(asJson<AssignableLease[]>),

  // staff users (admin only)
  users: () => json('/api/users').then(asJson<StaffUser[]>),
  createUser: async (
    input: { email: string; name: string; password: string; isAdmin: boolean },
  ): Promise<{ ok: true; row: StaffUser } | { ok: false; error: string }> => {
    const res = await json('/api/users', { method: 'POST', body: JSON.stringify(input) });
    if (res.status === 409) return { ok: false, error: 'A user with that email already exists.' };
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      return { ok: false, error: body?.error ?? 'creating the user failed' };
    }
    return { ok: true, row: (await res.json()) as StaffUser };
  },
  resetUserPassword: (id: number, password: string) =>
    json(`/api/users/${id}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }).then(asJson<{ ok: true }>),
  rerunReconciliation: (id: number) =>
    json(`/api/reconciliations/${id}/rerun`, { method: 'POST' }).then(
      asJson<{ id: number; summary: ReconSummary }>,
    ),
};
